import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A vertical lollipop chart is a variation of a bar chart that uses a thin line topped with
          a circular marker to represent data points. The height of the line corresponds to the
          value of the data, and the circular marker emphasizes the data point. This chart type is
          ideal for comparing values across categories while maintaining a minimalist and visually
          appealing design.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Vertical lollipop charts work best for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Highlighting Precise Values:</strong> They provide a clear visual representation
            of specific data points, such as sales figures, where the focus is on the endpoint
            rather than the entire bar.
          </li>
          <li>
            <strong>Simplifying Comparisons Across Categories:</strong> The minimal design makes it
            easy to compare values across categories, such as revenue by region or test scores by
            student group.
          </li>
          <li>
            <strong>Reducing Visual Clutter:</strong> Vertical lollipop charts are ideal for
            emphasizing data in a clean and straightforward manner, especially when the chart
            involves many categories or requires a sleek design.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Vertical lollipop charts are a valuable tool for displaying individual data points across
          categories, but they have certain limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Data Representation for Large Datasets:</strong> When dealing with a
            large number of categories, vertical lollipop charts can become cluttered, making it
            difficult to distinguish between individual data points and diminishing the chart&apos;s
            clarity.
          </li>
          <li>
            <strong>Challenges in Comparing Multiple Variables:</strong> Vertical lollipop charts
            are effective for visualizing a single variable at a time. When comparing multiple
            variables or trends across categories, other chart types, like grouped bar charts, may
            offer clearer insights.
          </li>
          <li>
            <strong>Inadequate for Representing Complex Data Relationships:</strong> For datasets
            with complex relationships or cumulative data, vertical lollipop charts may not
            effectively convey the nuances, as they are best suited for simple comparisons or
            single-variable analyses.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          For vertical lollipop charts, color selection plays a crucial role in ensuring clarity and
          comparison. Using distinct colors for each category helps differentiate data points,
          making it easier to identify trends and compare values. Subtle color variations can
          emphasize specific data series, while gradients may represent value progression or
          magnitude. A well-chosen color palette enhances the chart&apos;s visual appeal while
          preventing confusion and visual clutter, ensuring the data is communicated effectively.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Vertical lollipop charts are effective for visualizing individual data points, but they
          come with certain challenges that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Space for Categories:</strong> When there are many categories, vertical
            lollipop charts can become crowded, making it difficult to distinguish between data
            points and reducing the clarity of the visualization.
          </li>
          <li>
            <strong>Difficulty Comparing Values Across Categories:</strong> The length of the
            lollipop&apos;s stem can make it challenging to compare values across categories,
            especially when the differences between values are small.
          </li>
          <li>
            <strong>Visual Clutter with Multiple Series:</strong> Including multiple data series in
            a vertical lollipop chart can result in overlapping stems or markers, leading to
            confusion and making it harder to draw clear comparisons between categories.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
